import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminTradingAccounts.css';

import { API_URL } from '../config/data';

const AdminTradingAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [editingAccount, setEditingAccount] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    userId: '',
    currency: 'USD',
    percentage: 0,
    deposit_amount: 0
  });
  const [usersList, setUsersList] = useState([]);
  const [editForm, setEditForm] = useState({
    account_number: '',
    balance: '',
    profit: '',
    deposit_amount: '',
    percentage: '',
    currency: '',
    status: '',
    date: ''
  });

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchAccounts();
        await fetchUsersList();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate]);

  const fetchAccounts = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (filterStatus !== 'all') {
        params.append('isActive', filterStatus === 'active' ? 'true' : 'false');
      }
      if (filterCurrency !== 'all') {
        params.append('currency', filterCurrency);
      }

      const response = await axios.get(`${API_URL}/api/admin/trading-accounts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAccounts(response.data.accounts || []);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching trading accounts:', error);
      setError('Ошибка загрузки торговых счетов');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsersList(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users list:', error);
      setError('Ошибка загрузки списка пользователей');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAccounts(1);
  };

  const handleDeleteAccount = async (accountId, accountNumber) => {
    if (!window.confirm(`Вы уверены, что хотите удалить торговый счет ${accountNumber}? Это действие удалит счет и все связанные с ним операции. Это действие нельзя отменить.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_URL}/api/admin/trading-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchAccounts(currentPage);
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Ошибка удаления счета: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency || 'RUB'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/trading-accounts`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateModal(false);
      setCreateForm({ userId: '', currency: 'USD', percentage: 0, deposit_amount: 0 });
      await fetchAccounts(currentPage);
      setError('');
    } catch (error) {
      console.error('Error creating account:', error);
      setError(error.response?.data?.message || 'Ошибка создания счета');
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setEditForm({
      account_number: account.account_number || '',
      balance: account.balance || '',
      profit: account.profit || '',
      deposit_amount: account.deposit_amount || '',
      percentage: account.percentage || '',
      currency: account.currency || '',
      status: account.is_active ? 'active' : 'inactive',
      date: account.date || ''
    });
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/trading-accounts/${editingAccount.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Не закрываем модальное окно, только обновляем данные
      await fetchAccounts(currentPage);
      setError('');
    } catch (error) {
      console.error('Error updating account:', error);
      setError(error.response?.data?.message || 'Ошибка обновления счета');
    }
  };

  const handleLogout = () => {
    logoutAdmin();
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading__spinner"></div>
        <p>Загрузка торговых счетов...</p>
      </div>
    );
  }

  return (
    <div className="admin-trading-accounts">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Торговые счета</div>
        <div className="admin-header__actions">
          <button 
            className="admin-header__back" 
            onClick={() => navigate('/admin')}
          >
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={handleLogout}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button className="admin-btn admin-btn--primary" onClick={() => setError('')}>
              Закрыть
            </button>
          </div>
        )}

        <div className="admin-trading-accounts__filters">
          <div className="admin-filters-row">
            <form onSubmit={handleSearch} className="admin-search-form">
              <input
                type="text"
                placeholder="Поиск по номеру счета или владельцу..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input"
              />
              <button type="submit" className="admin-search-btn">
                <span className="--icon-search"></span>
              </button>
            </form>
            <button 
              className="admin-btn admin-btn--primary"
              onClick={() => setShowCreateModal(true)}
            >
              <span className="--icon-plus"></span>
              Создать счет
            </button>
          </div>

          <div className="admin-filters">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">Все статусы</option>
              <option value="active">Активные</option>
              <option value="inactive">Неактивные</option>
            </select>

            <select
              value={filterCurrency}
              onChange={(e) => setFilterCurrency(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">Все валюты</option>
              <option value="RUB">RUB</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>

        <div className="admin-trading-accounts__table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Номер счета</th>
                <th>Владелец</th>
                <th>Баланс</th>
                <th>Депозит</th>
                <th>Доход</th>
                <th>Доход (дата)</th>
                <th>Процент</th>
                <th>Валюта</th>
                <th>Статус</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td className="admin-table__id">{account.id}</td>
                  <td className="admin-table__account-number">
                    {account.account_number}
                  </td>
                  <td className="admin-table__owner">
                    <div className="admin-table__owner-info">
                      <div className="admin-table__owner-name">
                        {account.first_name} {account.last_name}
                      </div>
                      <div className="admin-table__owner-email">
                        {account.email}
                      </div>
                    </div>
                  </td>
                  <td className="admin-table__balance">
                    {formatCurrency(account.balance, account.currency)}
                  </td>
                  <td className="admin-table__deposit">
                    {formatCurrency(account.deposit_amount || 0, account.currency)}
                  </td>
                  <td className="admin-table__profit">
                    {formatCurrency(account.profit || 0, account.currency)}
                  </td>
                  <td className='admin-table__date'>
                    {account.date}
                  </td>
                  <td className="admin-table__percentage">
                    {account.percentage ? `${account.percentage}%` : '0%'}
                  </td>
                  <td className="admin-table__currency">
                    {account.currency}
                  </td>
                  <td className="admin-table__status">
                    <span className={`admin-status admin-status--${account.is_active ? 'active' : 'inactive'}`}>
                      {account.is_active ? 'Активный' : 'Неактивный'}
                    </span>
                  </td>
                  <td className="admin-table__date">
                    {formatDate(account.created_at)}
                  </td>
                  <td className="admin-table__actions">
                    <div className="admin-table__action-buttons">
                      <button
                        className="admin-btn admin-btn--primary"
                        onClick={() => handleEditAccount(account)}
                      >
                        Изменить
                      </button>
                      <button
                        className="admin-btn admin-btn--danger"
                        onClick={() => handleDeleteAccount(account.id, account.account_number)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {accounts.length === 0 && (
            <div className="admin-empty">
              <h2>Торговые счета не найдены</h2>
              <p>Попробуйте изменить параметры поиска или фильтры</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => fetchAccounts(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            
            <div className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </div>
            
            <button
              className="admin-pagination__btn"
              onClick={() => fetchAccounts(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}

        {showCreateModal && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setShowCreateModal(false)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Создать торговый счет</h2>
                <button 
                  className="admin-modal__close"
                  onClick={() => setShowCreateModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <form onSubmit={handleCreateAccount} className="admin-edit-form">
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="userId">Пользователь *</label>
                      <select
                        id="userId"
                        value={createForm.userId}
                        onChange={(e) => setCreateForm({...createForm, userId: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      >
                        <option value="">Выберите пользователя</option>
                        {usersList.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.fullName} ({user.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="currency">Валюта *</label>
                      <select
                        id="currency"
                        value={createForm.currency}
                        onChange={(e) => setCreateForm({...createForm, currency: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      >
                        <option value="RUB">RUB</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="percentage">Процент *</label>
                      <input
                        type="number"
                        id="percentage"
                        step="0.01"
                        value={createForm.percentage}
                        onChange={(e) => setCreateForm({...createForm, percentage: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="deposit_amount">Депозит *</label>
                      <input
                        type="number"
                        id="deposit_amount"
                        step="0.01"
                        value={createForm.deposit_amount}
                        onChange={(e) => setCreateForm({...createForm, deposit_amount: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                  </div>
                  <div className="admin-modal__footer">
                    <button type="button" className="admin-btn admin-btn--secondary" onClick={() => setShowCreateModal(false)}>
                      Отмена
                    </button>
                    <button type="submit" className="admin-btn admin-btn--primary">
                      Создать
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {editingAccount && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setEditingAccount(null)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Редактировать торговый счет</h2>
                <button 
                  className="admin-modal__close"
                  onClick={() => setEditingAccount(null)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <form onSubmit={handleUpdateAccount} className="admin-edit-form">
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="account_number">Номер счета *</label>
                      <input
                        type="text"
                        id="account_number"
                        value={editForm.account_number}
                        onChange={(e) => setEditForm({...editForm, account_number: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="balance">Баланс *</label>
                      <input
                        type="number"
                        id="balance"
                        step="0.01"
                        value={editForm.balance}
                        onChange={(e) => setEditForm({...editForm, balance: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="profit">Доход *</label>
                      <input
                        type="number"
                        id="profit"
                        step="0.01"
                        value={editForm.profit}
                        onChange={(e) => setEditForm({...editForm, profit: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>

                    <div className="admin-edit-form__field">
                      <label htmlFor="date">Доход (дата)</label>
                      <input
                        type="text"
                        id="date"
                        value={editForm.date}
                        onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                        className="admin-edit-form__input"
                        placeholder="Пример: 21.02.2025"
                      />

                  </div>
                  </div>
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="deposit_amount">Депозит *</label>
                      <input
                        type="number"
                        id="deposit_amount"
                        step="0.01"
                        value={editForm.deposit_amount}
                        onChange={(e) => setEditForm({...editForm, deposit_amount: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="percentage">Процент *</label>
                      <input
                        type="number"
                        id="percentage"
                        step="0.01"
                        value={editForm.percentage}
                        onChange={(e) => setEditForm({...editForm, percentage: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                  </div>
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="currency">Валюта *</label>
                      <select
                        id="currency"
                        value={editForm.currency}
                        onChange={(e) => setEditForm({...editForm, currency: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      >
                        <option value="RUB">RUB</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="status">Статус *</label>
                      <select
                        id="status"
                        value={editForm.status}
                        onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                        className="admin-edit-form__input"
                        required
                      >
                        <option value="active">Активный</option>
                        <option value="inactive">Неактивный</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="admin-modal__footer">
                    <button type="button" className="admin-btn admin-btn--secondary" onClick={() => setEditingAccount(null)}>
                      Отмена
                    </button>
                    <button type="submit" className="admin-btn admin-btn--primary">
                      Сохранить
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTradingAccounts;
